import moment from 'moment';
import {
  toastSaveSuccess,
  toastSaveDelete,
  toastError,
  showActionConfirmation,
} from 'Common/utilities/alert';
import {
  NZ_DATE_INPUT_FORMAT,
  TO_MYCRM_DATE_FORMAT,
  DD_MMM_YY_FORMAT,
  DD_MM_YYYY_FORMAT,
  UI_MASK_DATE,
} from 'Common/constants/dateConfigs';
import {
  INPUT_DATE_FORMAT,
  ALT_INPUT_FORMATS,
  DATE_OPTIONS,
} from 'Common/constants/workbench';
import { DEFERRED_STATUS_ID } from 'Common/constants/insuranceApplicationPipeline';

export function mapBenefitDetailsStatus(benefitDetails, statuses) {
  return (
    statuses &&
    benefitDetails &&
    benefitDetails.map((detail) => {
      const status = statuses.find((stat) => stat.name === detail.status);
      return {
        ...detail,
        statusId: status ? status.id : 0,
      };
    })
  );
}

export function updateBenefitDetailInList(updatedBenefitDetail, list) {
  if (!updatedBenefitDetail || !list) {
    return list;
  }
  const indexOfBenefitCover = list.findIndex(
    (detail) => detail.id === updatedBenefitDetail.id,
  );
  return [
    ...list.slice(0, indexOfBenefitCover),
    { ...updatedBenefitDetail },
    ...list.slice(indexOfBenefitCover + 1),
  ];
}

export default class InsuranceAppCoverDetailsCtrl {
  constructor(insurancePipelineService, insuranceModalApplicationService) {
    'ngInject';

    this.insurancePipelineService = insurancePipelineService;
    this.insuranceModalApplicationService = insuranceModalApplicationService;
    this.INPUT_DATE_FORMAT = INPUT_DATE_FORMAT;
    this.ALT_INPUT_FORMATS = ALT_INPUT_FORMATS;
    this.DATE_OPTIONS = DATE_OPTIONS;
    this.DEFERRED_STATUS_ID = DEFERRED_STATUS_ID.V3;
  }

  $onInit() {
    this.loadStatuses().then(() => this.loadCoverDetails());
    this.moment = moment;
    this.DD_MM_YYYY_FORMAT = DD_MM_YYYY_FORMAT.toLowerCase();
    this.UI_MASK_DATE = UI_MASK_DATE;
    this.DD_MMM_YY_FORMAT = DD_MMM_YY_FORMAT;
    this.NZ_DATE_INPUT_FORMAT = NZ_DATE_INPUT_FORMAT;

    this.onInit &&
      this.onInit({
        api: {
          refresh: () => this.loadCoverDetails(),
        },
      });
  }

  loadStatuses() {
    return this.insurancePipelineService
      .getInsurancePipelineStatus(null, false)
      .then((response) => {
        this.statuses = response;
        return response;
      });
  }

  loadCoverDetails() {
    this.coverDetails = {};
    this.insurancePipelineService
      .getInsuranceAppCoverDetails(this.quoteId)
      .then((response) => {
        if (!response) {
          return;
        }
        this.coverDetails = {
          ...response,
          benefitDetails: mapBenefitDetailsStatus(
            response.benefitDetails,
            this.statuses,
          ),
        };
      });
  }

  openDate(field) {
    switch (field) {
      case 'dateStarted':
        this.dateStartedIsOpen = !this.dateStartedIsOpen;
        break;
      case 'dateSubmitted':
        this.dateSubmittedIsOpen = !this.dateSubmittedIsOpen;
        break;
      default:
    }
  }

  editStatusInline(details, toEdit = true) {
    this.ALT_INPUT_FORMATS = ALT_INPUT_FORMATS;
    details.isEditMode = toEdit;
    if (toEdit || details.isSaved) {
      details.isSaved = false;
      details.tempStatusId = details.statusId;
      details.tempStatus = details.status;
      details.tempPolicyNumber = details.policyNumber;
    } else {
      details.statusId = details.tempStatusId;
      details.status = details.tempStatus;
      details.policyNumber = details.tempPolicyNumber;
    }
  }

  toggleEditBenefitCover(id) {
    this.insurancePipelineService.getBenefitCover(id).then((response) => {
      this.insuranceModalApplicationService
        .openAddBenefitModal({
          ...response,
          familyId: this.familyId,
          paymentFrequency: this.getPaymentFrequency(),
        })
        .result.then(() => {
          this.loadCoverDetails();
          this.onUpdated && this.onUpdated();
        });
    });
  }

  showAddNewBenefitForm() {
    const data = {
      quoteId: this.quoteId,
      familyId: this.familyId,
      providerId: this.getInsurerId(),
      paymentFrequency: this.getPaymentFrequency(),
      providerResultId: this.coverDetails.providerResultId,
    };
    this.insuranceModalApplicationService
      .openAddBenefitModal(data)
      .result.then(() => {
        this.loadCoverDetails();
        this.onUpdated && this.onUpdated();
      });
  }

  getInsurerId() {
    return this.coverDetails.insurer.id;
  }

  getPaymentFrequency() {
    const { benefitDetails } = this.coverDetails;
    if (!benefitDetails || !benefitDetails.length) {
      return;
    }
    return benefitDetails[0].frequency;
  }

  saveBenefitCover(id) {
    const { benefitDetails } = this.coverDetails;
    const benefitCover = benefitDetails.find((detail) => detail.id === id);
    if (!benefitCover) {
      return;
    }

    const { statusId, policyNumber, tempDeferralReviewDate } = benefitCover;
    const deferralReviewDate =
      tempDeferralReviewDate &&
      moment(tempDeferralReviewDate, NZ_DATE_INPUT_FORMAT).format(
        TO_MYCRM_DATE_FORMAT,
      );

    const isValidateDeferDate =
      statusId === this.DEFERRED_STATUS_ID &&
      (!tempDeferralReviewDate || deferralReviewDate === 'Invalid date');
    if (isValidateDeferDate) {
      toastError('Valid Deferral Review Date is required.');
      return false;
    }

    this.insurancePipelineService
      .saveInsuranceAppBenefitCover(id, {
        statusId,
        policyNumber,
        deferralReviewDate,
      })
      .then(() => {
        benefitCover.copy = null;
        benefitCover.isSaved = true;
        this.editStatusInline(benefitCover, false);
        this.onUpdated && this.onUpdated();
        toastSaveSuccess();
      })
      .catch(toastError);
  }

  deleteBenefitCover(id) {
    showActionConfirmation(
      'Are you sure you want to delete the benefit cover?',
      'You will not be able to retrieve this later.',
      (confirm) => {
        if (!confirm) {
          return;
        }
        this.insurancePipelineService
          .deleteInsuranceAppBenefitCover(id)
          .then(() => {
            toastSaveDelete();
            const { benefitDetails } = this.coverDetails;
            const indexOfDeletedCover = benefitDetails.findIndex(
              (detail) => detail.id === id,
            );
            this.coverDetails.benefitDetails = [
              ...benefitDetails.slice(0, indexOfDeletedCover),
              ...benefitDetails.slice(indexOfDeletedCover + 1),
            ];
            this.onUpdated && this.onUpdated();
          })
          .catch(toastError);
      },
    );
  }

  onDateChange() {
    const { dateSubmitted, dateStarted } = this.coverDetails;
    const params = {
      dateSubmitted:
        dateSubmitted && moment(dateSubmitted).format(TO_MYCRM_DATE_FORMAT),
      dateStarted:
        dateStarted && moment(dateStarted).format(TO_MYCRM_DATE_FORMAT),
    };
    this.insurancePipelineService
      .saveInsuranceAppCoverDetails(this.quoteId, params)
      .then(() => toastSaveSuccess())
      .catch(toastError);
  }

  onSelectStatus(benefitCoverId, statusId) {
    const { benefitDetails } = this.coverDetails;
    const benefitCover = benefitDetails.find(
      (detail) => detail.id === benefitCoverId,
    );
    const status = this.statuses.find((stat) => stat.id === statusId);
    benefitCover.status = status.name;
  }
}
